import { markRaw } from 'vue';
import { type Step, type BookingModeSteps, BookingMode } from '../types/order-main';
import StepOrderRequest from '../components/step-order-request.vue';
import StepOrderer from '../components/step-orderer.vue';
import StepMph from '../components/step-mph.vue';
import StepProduct from '../components/step-product/step-product.vue';
import StepCustomer from '../components/step-customer/step-customer.vue';
import StepOwner from '../components/step-owner.vue';
import StepSummaryAndBooking from '../components/step-summary-and-booking.vue';

const allSteps: Step[] = [
  {
    name: 'StepOrderRequest',
    icon: '',
    title: 'Auftragsanfrage',
    sequence: 1,
    isCompleted: false,
    isDisabled: false,
    isActive: true,
    component: markRaw(StepOrderRequest),
  },
  {
    name: 'StepOrderer',
    icon: '',
    title: 'Auftraggeberdaten',
    sequence: 2,
    isCompleted: false,
    isDisabled: false,
    isActive: false,
    component: markRaw(StepOrderer),
  },
  {
    name: 'StepMph',
    icon: '',
    title: 'Gebäude-Glasfaser-Netz',
    sequence: 3,
    isCompleted: false,
    isDisabled: false,
    isActive: false,
    component: markRaw(StepMph),
  },
  {
    name: 'StepProduct',
    icon: '',
    title: 'Produktkonfiguration',
    sequence: 4,
    isCompleted: false,
    isDisabled: false,
    isActive: false,
    component: markRaw(StepProduct),
  },
  {
    name: 'StepCustomer',
    icon: '',
    title: 'Kundendaten',
    sequence: 5,
    isCompleted: false,
    isDisabled: false,
    isActive: false,
    component: markRaw(StepCustomer),
  },
  {
    name: 'StepOwner',
    icon: '',
    title: 'Eigentümerinformationen',
    sequence: 6,
    isCompleted: false,
    isDisabled: false,
    isActive: false,
    component: markRaw(StepOwner),
  },
  {
    name: 'StepSummaryAndBooking',
    icon: '',
    title: 'Abschluss',
    sequence: 7,
    isCompleted: false,
    isDisabled: false,
    isActive: false,
    component: markRaw(StepSummaryAndBooking),
  },
];

const stepsInModes: BookingModeSteps[] = [
  {
    name: BookingMode.houseConnectionOnly,
    availableSteps: ['StepOrderRequest', 'StepOrderer', 'StepSummaryAndBooking'],
  },
  {
    name: BookingMode.houseConnectionAndNe4,
    availableSteps: ['StepOrderRequest', 'StepOrderer', 'StepMph', 'StepSummaryAndBooking'],
  },
  {
    name: BookingMode.ne4Only,
    availableSteps: ['StepOrderRequest', 'StepOrderer', 'StepMph', 'StepSummaryAndBooking'],
  },
  {
    name: BookingMode.houseConnectionAndProduct,
    availableSteps: [
      'StepOrderRequest',
      'StepOrderer',
      'StepProduct',
      'StepCustomer',
      'StepSummaryAndBooking',
    ],
  },
  {
    name: BookingMode.ne4AndProduct,
    availableSteps: [
      'StepOrderRequest',
      'StepOrderer',
      'StepMph',
      'StepProduct',
      'StepCustomer',
      'StepSummaryAndBooking',
    ],
  },
  {
    name: BookingMode.productOnly,
    availableSteps: [
      'StepOrderer',
      'StepProduct',
      'StepCustomer',
      'StepSummaryAndBooking',
    ],
  },
  {
    name: BookingMode.productOnlyMph,
    availableSteps: [
      'StepOrderer',
      'StepProduct',
      'StepCustomer',
      'StepOwner',
      'StepSummaryAndBooking',
    ],
  },
  {
    name: BookingMode.houseConnectionAndNe4AndProduct,
    availableSteps: [
      'StepOrderRequest',
      'StepOrderer',
      'StepMph',
      'StepProduct',
      'StepCustomer',
      'StepSummaryAndBooking',
    ],
  },
];

/**
 * @param mode
 * @returns Step[] - array os steps which are available for desired order type
 */
function getSteps(mode: BookingMode): Step[] {
  const { availableSteps } = stepsInModes.find(({ name }: BookingModeSteps) => name === mode)!;

  return allSteps
    .filter((step: Step) => availableSteps.includes(step.name))
    .sort((a, b) => a.sequence - b.sequence);
}

function getAvailableStepsSequences(mode = BookingMode.houseConnectionOnly): number[] {
  const { availableSteps } = stepsInModes.find(({ name }: BookingModeSteps) => name === mode)!;
  return availableSteps
    .map((stepName: string) => allSteps.find((step: Step) => step.name === stepName)!.sequence)
    .sort((a, b) => a - b);
}

export { getSteps, getAvailableStepsSequences };
/**
 * sequence as array index!!
 */
